import React from "react";
import { Link } from "react-router-dom";
import { Link2, Compass, MousePointerClick, ArrowRight, Undo2, ShieldCheck } from "lucide-react";

import SectionLabel from "./SectionLabel";
import { CONNECTORS } from "./aigoMetrics";

const steps = [
  {
    n: 1,
    icon: <Link2 className="w-5 h-5" />,
    kicker: "Connect",
    title: "One-click Shopify install + your stack.",
    body: `AIGO reads live data from ${CONNECTORS.join(", ")}. No CSVs. No pipelines. No consultants.`,
  },
  {
    n: 2,
    icon: <Compass className="w-5 h-5" />,
    kicker: "See what changed & why",
    title: "Growth Score + Top Moves — plain English.",
    body: "A 0–100 score across 6 pillars. Every leak has a $/mo impact and a HIGH/MEDIUM confidence tag.",
  },
  {
    n: 3,
    icon: <MousePointerClick className="w-5 h-5" />,
    kicker: "Approve in one click",
    title: "Preview first. Ship in one tap. Undo anytime.",
    body: "Every action runs in preview_mode until you flip live execution. Safe by default. You approve every move.",
  },
];

export const HowItWorks = () => (
  <section
    id="how"
    data-testid="how-section"
    className="px-6 md:px-10 py-20 md:py-24 border-t border-slate-100 bg-slate-50"
  >
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-14">
        <SectionLabel>How it works</SectionLabel>
        <h2 className="text-3xl md:text-5xl lg:text-6xl text-slate-900 max-w-4xl mx-auto mt-4 mb-4 tracking-tight font-bold leading-[1.05]">
          Three simple steps to <span className="text-emerald-600">CFO-grade clarity.</span>
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
          Live in under 60 seconds. No card. No commitment. Nothing ships to your store without your explicit approval.
        </p>
      </div>

      <div className="relative grid md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
        {/* Connecting line (desktop) */}
        <div
          aria-hidden
          className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-px"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to right, #cbd5e1 0 6px, transparent 6px 12px)",
          }}
        />

        {steps.map((s) => (
          <div
            key={s.n}
            data-testid={`step-${s.n}`}
            className="relative rounded-3xl border border-slate-200 bg-white p-6 md:p-7 shadow-sm hover:shadow-md hover:border-emerald-300 hover:-translate-y-0.5 transition"
          >
            {/* Step number badge */}
            <div className="absolute -top-3 left-6 w-8 h-8 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center shadow-lg shadow-slate-900/20">
              {s.n}
            </div>
            <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-emerald-700">
              <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                {s.icon}
              </span>
              {s.kicker}
            </div>
            <h3 className="mt-3 text-slate-900 text-lg md:text-xl font-bold tracking-tight leading-tight">
              {s.title}
            </h3>
            <p className="mt-2 text-slate-600 text-sm md:text-base leading-relaxed">
              {s.body}
            </p>
          </div>
        ))}
      </div>

      {/* Safety strip */}
      <div className="mt-10 md:mt-12 max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm text-slate-600 text-center">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Safe by default (preview_mode)
        </span>
        <span className="hidden sm:inline text-slate-300">·</span>
        <span className="inline-flex items-center gap-1.5">
          <Undo2 className="w-4 h-4 text-emerald-600" /> Undo any implementation
        </span>
        <span className="hidden sm:inline text-slate-300">·</span>
        <Link to="/privacy" data-testid="how-privacy-link" className="text-slate-600 hover:text-slate-900 transition inline-flex items-center gap-1">
          Privacy <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  </section>
);

export default HowItWorks;
